/**
 * Permission helper for the iRepair POS frontend.
 *
 * This file mirrors `/app/backend/permissions.py` — same keys, same group
 * ordering, same staff defaults. We deliberately duplicate the catalog
 * instead of fetching it at boot because:
 *   1. The UI needs to render conditional content (e.g. hide the REFUND
 *      button) on first paint, before any HTTP round-trip.
 *   2. Permission checks happen on every render — going through React
 *      state from a fetched catalog adds boilerplate for no win.
 *
 * The backend remains the source of truth: even if this file drifts, the
 * server still 403s any unauthorised request. This is purely a UX layer.
 */

export const PERMISSIONS = [
  // ---------- Sales ----------
  { key: "sale.create",      group: "Sales",          label: "Create sales",                          staff_default: true  },
  { key: "sale.edit",        group: "Sales",          label: "Edit invoice",                          staff_default: false },
  { key: "sale.void",        group: "Sales",          label: "Void a sale",                           staff_default: false },
  { key: "sale.refund",      group: "Sales",          label: "Issue refunds",                         staff_default: false },
  { key: "sale.delete",      group: "Sales",          label: "Delete sale permanently",               staff_default: false },
  // ---------- Repair tickets ----------
  { key: "ticket.create",    group: "Repair tickets", label: "Create repair tickets",                 staff_default: true  },
  { key: "ticket.edit",      group: "Repair tickets", label: "Edit ticket details",                   staff_default: true  },
  { key: "ticket.status",    group: "Repair tickets", label: "Change ticket status",                  staff_default: true  },
  { key: "ticket.delete",    group: "Repair tickets", label: "Delete tickets",                        staff_default: false },
  // ---------- Inventory ----------
  { key: "inventory.view",   group: "Inventory",      label: "Browse products & stock",               staff_default: true  },
  { key: "inventory.edit",   group: "Inventory",      label: "Add or edit products",                  staff_default: false },
  { key: "inventory.delete", group: "Inventory",      label: "Delete products",                       staff_default: false },
  { key: "inventory.import", group: "Inventory",      label: "Bulk import / wholesale upload",        staff_default: false },
  // ---------- Parts Inventory ----------
  { key: "parts.view",      group: "Parts Inventory", label: "Browse repair parts & stock levels",    staff_default: true  },
  { key: "parts.receive",   group: "Parts Inventory", label: "Receive shipments & customer returns",  staff_default: true  },
  { key: "parts.adjust",    group: "Parts Inventory", label: "Stock corrections & write-offs",        staff_default: false },
  { key: "parts.edit",      group: "Parts Inventory", label: "Edit price, cost & link/unlink parts",  staff_default: false },
  { key: "parts.transfer",  group: "Parts Inventory", label: "Transfer stock between stores",         staff_default: false },
  { key: "parts.delete",    group: "Parts Inventory", label: "Delete shared parts",                   staff_default: false },
  // ---------- Customers ----------
  { key: "customer.view",    group: "Customers",      label: "Browse customers",                      staff_default: true  },
  { key: "customer.edit",    group: "Customers",      label: "Add or edit customers",                 staff_default: true  },
  { key: "customer.delete",  group: "Customers",      label: "Delete customers",                      staff_default: false },
  { key: "customer.import",  group: "Customers",      label: "Bulk import customers",                 staff_default: false },
  // ---------- Trade-Ins ----------
  { key: "tradein.view",     group: "Trade-Ins",      label: "Browse trade-ins",                      staff_default: true  },
  { key: "tradein.create",   group: "Trade-Ins",      label: "Create new trade-in intakes",           staff_default: true  },
  { key: "tradein.process",  group: "Trade-Ins",      label: "Process / pay out trade-ins",           staff_default: false },
  // ---------- Store ----------
  { key: "store.switch",     group: "Store",          label: "Switch stores without admin PIN",       staff_default: false },
  // ---------- Reports ----------
  { key: "report.view",      group: "Reports",        label: "View dashboard & financial reports",    staff_default: false },
  // ---------- Settings ----------
  { key: "settings.view",    group: "Settings",       label: "Open the Settings area",                staff_default: false },
  { key: "settings.edit",    group: "Settings",       label: "Change settings (integrations, etc.)",  staff_default: false },
  // ---------- Till ----------
  { key: "till.open",              group: "Till",           label: "Open shift",                             staff_default: true  },
  { key: "till.close",             group: "Till",           label: "Close shift",                            staff_default: true  },
  { key: "till.adjust",            group: "Till",           label: "Manual cash drawer adjustments",         staff_default: false },
  { key: "till.cashouts.view_own", group: "Till",           label: "View own past cash-outs",                staff_default: false },
];

// Staff defaults map for quick lookup (e.g. when initialising a fresh
// staff user that hasn't been customised yet).
export const STAFF_DEFAULTS = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.staff_default]));

// Ordered list of groups (drives the modal's section order).
export const GROUPS = Array.from(new Set(PERMISSIONS.map((p) => p.group)));

// Template presets — must match backend exactly.
export const TEMPLATES = {
  cashier: {
    label: "Cashier",
    description: "Ring sales, take payments, intake repair tickets. No destructive actions.",
    permissions: { ...STAFF_DEFAULTS },
  },
  senior_staff: {
    label: "Senior staff",
    description: "Cashier + edit invoices, delete tickets, switch stores, process trade-ins.",
    permissions: {
      ...STAFF_DEFAULTS,
      "sale.edit": true,
      "ticket.delete": true,
      "tradein.process": true,
      "store.switch": true,
    },
  },
  manager: {
    label: "Manager",
    description: "Senior staff + void, refund, manage inventory, view reports.",
    permissions: {
      ...STAFF_DEFAULTS,
      "sale.edit": true,
      "sale.void": true,
      "sale.refund": true,
      "ticket.delete": true,
      "inventory.edit": true,
      "inventory.import": true,
      "parts.adjust": true,
      "parts.edit": true,
      "parts.transfer": true,
      "tradein.process": true,
      "store.switch": true,
      "report.view": true,
      "settings.view": true,
      "till.adjust": true,
    },
  },
};

/**
 * Check whether a user has a given permission. Admins always do.
 * Returns a stable false if the user object isn't loaded yet so the UI
 * fails closed (rather than briefly flashing privileged buttons before
 * AuthContext resolves).
 */
export function userCan(user, key) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const map = user.permissions || {};
  if (Object.prototype.hasOwnProperty.call(map, key)) return !!map[key];
  // Fall back to staff defaults if the server hasn't materialised the map.
  return !!STAFF_DEFAULTS[key];
}

/**
 * Decide which preset best matches a permission state. Used by the
 * admin Permissions modal to flip the dropdown when an admin toggles
 * something that exactly matches a known template (or back to "Custom"
 * once they deviate).
 */
export function detectTemplate(currentPermissions) {
  for (const [key, tpl] of Object.entries(TEMPLATES)) {
    const target = tpl.permissions;
    if (PERMISSIONS.every((p) => !!currentPermissions[p.key] === !!target[p.key])) {
      return key;
    }
  }
  return "custom";
}
